import { ApolloQueryResult, useQuery } from '@apollo/client';
import { RoomFilter, RoomItem, RoomPaginationResponse } from '../types';
import { useState } from 'react';
import { PageInfo, defaultPageInfo } from 'lib/enums/pageInfo';
import { GET_ROOMS_PAGINATION } from 'pagesAdmin/SettingsPage/gqlSchemes';
import { useDebounce } from 'use-debounce';

type GetRoomsAutocompleteVariables = {
  filter?: RoomFilter;
  page?: number;
  perPage?: number;
};

const useGetRoomsAutocomplete = ({
  filter,
}: {
  initialPage?: number;
  perPage?: number;
  filter?: {
    locationId: string;
    reservationModeId: string;
  };
} = {}) => {
  const [items, setItems] = useState<RoomItem[]>([]);
  const [pageInfo, setPageInfo] = useState<PageInfo>(defaultPageInfo);
  const [currentPage, setCurrentPage] = useState(0);

  const [search, setSearch] = useState<string>('');
  const [debouncedSearch] = useDebounce(search, 300);

  const { loading, fetchMore } = useQuery<
    { schedule: { roomPagination: RoomPaginationResponse } },
    GetRoomsAutocompleteVariables
  >(GET_ROOMS_PAGINATION, {
    variables: {
      filter: { name: debouncedSearch, ...filter },
      page: 0,
      perPage: 10,
    },
    onCompleted: (queryData) => {
      if (queryData?.schedule.roomPagination) {
        setItems(queryData.schedule.roomPagination.items);
        setPageInfo(queryData.schedule.roomPagination.pageInfo);
      }
    },
  });

  const loadMore = async () => {
    if (pageInfo.hasNextPage) {
      const nextPage = currentPage + 1;
      const result: ApolloQueryResult<{
        schedule: { roomPagination: RoomPaginationResponse };
      }> = await fetchMore({
        variables: {
          filter: { name: debouncedSearch },
          page: nextPage,
          perPage: 10,
        },
      });

      if (result.data.schedule.roomPagination) {
        setItems((prevItems) => [
          ...prevItems,
          ...result.data.schedule.roomPagination.items,
        ]);
        setPageInfo(result.data.schedule.roomPagination.pageInfo);
        setCurrentPage(nextPage);
      }
    }
  };

  return {
    rooms: items,
    pageInfo,
    loading,
    setSearch,
    loadMore,
  };
};

export default useGetRoomsAutocomplete;
